import React, { useEffect } from "react";
import {
  Autocomplete,
  FormControlLabel,
  InputAdornment,
  Switch,
  TextField,
} from "@mui/material";
import CustomDatePicker from "./CustomDatePicker";
import CustomSelect from "./CustomSelect";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";

const CustomTextField = ({
  field,
  data = {},
  setData,
  errors,
  setErrors,
  autoFocus,
  disabled,
  editable,
}) => {
  const handleChange = (e, newValue) => {
    let value = e?.target ? e.target.value : newValue;

    if (field.type === "autocomplete") {
      value = newValue || e?.target?.value || "";
    }

    if (field.type === "date") {
      setData((prev) => ({
        ...prev,
        [field.name]: e ? e : "",
      }));

      if (field.required) {
        setErrors((prevErrors) => ({
          ...prevErrors,
          [field.name]: !e,
        }));
      }

      return;
    }

    if (field.type === "number") {
      value = value.replace(",", ".");

      let regex;
      if (field.decimals === 0) {
        regex = /^\d+$/;
      } else {
        regex = new RegExp(`^\\d+(\\.\\d{0,${field.decimals}})?$`);
      }

      if (!regex.test(value) && value !== "") return;
    }

    setData((prev) => ({
      ...prev,
      [field.name]: value,
    }));

    if (errors[field.name]) {
      setErrors((prevErrors) => ({
        ...prevErrors,
        [field.name]: false,
      }));
    }
  };

  if (field.type === "date") {
    return (
      <CustomDatePicker
        value={data[field.name] || null}
        onChange={handleChange}
        field={field}
        error={errors[field.name]}
        disabled={!editable}
      />
    );
  }

  if (field.type === "select") {
    return (
      <CustomSelect
        value={data[field.name] || ""}
        onChange={handleChange}
        field={field}
        error={errors[field.name]}
      />
    );
  }

  if (field.type === "switch") {
    return (
      <FormControlLabel
        control={
          <Switch
            checked={Boolean(data[field.name])}
            onChange={(e) =>
              setData((prev) => ({
                ...prev,
                [field.name]: e.target.checked,
              }))
            }
            disabled={disabled || !editable}
          />
        }
        label={field.label}
      />
    );
  }

  if (field.type === "autocomplete") {
    return (
      <Autocomplete
        freeSolo
        options={field.options || []}
        disableClearable
        value={
          data[field.name] ||
          (process.env.NEXT_PUBLIC_DUMMY_DATA === "TRUE" &&
            field.defaultValue) ||
          ""
        }
        onChange={(e, newValue) => handleChange(e, newValue)}
        onInputChange={(e, newInputValue) => handleChange(e, newInputValue)}
        renderInput={(params) => (
          <TextField
            {...params}
            label={field.label}
            variant="outlined"
            fullWidth
            margin="dense"
            required={field.required}
            error={errors[field.name]}
            helperText={
              errors[field.name]
                ? "This field is required"
                : field.helperText || ""
            }
            size="small"
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {params.InputProps.endAdornment}
                  <InputAdornment position="end">
                    <ArrowDropDownIcon />
                  </InputAdornment>
                </>
              ),
            }}
          />
        )}
      />
    );
  }

  return (
    <TextField
      label={field.label}
      variant="outlined"
      fullWidth
      margin="dense"
      required={field.required}
      value={data[field.name] || ""}
      onChange={handleChange}
      error={errors[field.name]}
      autoFocus={autoFocus}
      helperText={
        errors[field.name] ? "This field is required" : field.helperText || ""
      }
      disabled={disabled || !editable}
      size={"small"}
      InputProps={{
        startAdornment: field.startAdornment && (
          <InputAdornment position="start">
            {field.startAdornment}
          </InputAdornment>
        ),
        endAdornment: field.endAdornment && (
          <InputAdornment position="end">{field.endAdornment}</InputAdornment>
        ),
      }}
    />
  );
};

export default CustomTextField;
